(() => {
  'use strict';
  if (window.IVRKitBuilderV3) {
    window.IVRKitBuilderV3.init(document);
    return;
  }

  const ROOT = '[data-ivr-kit-builder]';
  const normalize = (value) => String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();
  const optionAt = (variant, index) => index < 0 ? null : variant['option' + (index + 1)];
  const COLORS = {
    preto:'#171717',black:'#171717',branco:'#f6f6f4',white:'#f6f6f4',offwhite:'#eee8dc',
    'off white':'#eee8dc',chocolate:'#664032',marrom:'#68432e',brown:'#68432e',
    'azul marinho':'#1e3050',marinho:'#1e3050',navy:'#1e3050',azul:'#39618a',
    cinza:'#969696',grey:'#969696',gray:'#969696',oliva:'#526144',
    verde:'#536c4c','verde militar':'#48533c',vinho:'#74273d',
    bege:'#d0b99a',areia:'#cbbda6',rosa:'#c99aa2',vermelho:'#a63432'
  };
  const preferredSizes = ['PP','P','M','G','GG','XGG','XXG','EG','EXG','U'];
  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = String(text);
    return node;
  };


  function parsePriceCents(value) {
    let cleaned=String(value ?? '').trim().replace(/[^\d,.-]/g,'');
    if(!cleaned || cleaned==='-')return null;
    const comma=cleaned.lastIndexOf(','),dot=cleaned.lastIndexOf('.');
    if(comma>=0 && dot>=0){
      cleaned=comma>dot ? cleaned.replace(/\./g,'').replace(',','.') : cleaned.replace(/,/g,'');
    }else if(comma>=0){
      cleaned=cleaned.replace(/\./g,'').replace(',','.');
    }else if(dot>=0 && cleaned.length-dot-1===3){
      cleaned=cleaned.replace(/\./g,'');
    }
    const parsed=Number(cleaned);
    if(!Number.isFinite(parsed) || parsed<0)return null;
    return Math.round(parsed*100);
  }

  class KitBuilder {
    constructor(root, data) {
      this.root = root;
      this.data = data;
      this.config = data.config || {};
      this.products = Array.isArray(data.products) ? data.products.filter((p) => p?.id && Array.isArray(p.variants) && p.variants.length) : [];
      this.tiers = Array.isArray(data.tiers) ? data.tiers.map((tier) => ({
        ...tier,
        count: Math.max(1, Math.min(12, Number(tier.count) || 1)),
        compareCents: parsePriceCents(tier.comparePrice),
        saleCents: parsePriceCents(tier.salePrice)
      })) : [];
      this.maxCount = Math.max(1, ...this.tiers.map((tier) => tier.count));
      this.tierIndex = Math.min(Math.max(Number(this.config.defaultTier) || 0, 0), Math.max(0, this.tiers.length - 1));
      this.slots = Array.from({length:this.maxCount}, () => null);
      this.modalSlot = -1;
      this.lastModalSlot = -1;
      this.nextModalSlot = -1;
      this.draft = null;
      this.slideIndex = 0;
      this.busy = false;
      this.abort = new AbortController();
      this.dialog = root.querySelector('[data-kit-dialog]');
      this.colorChoices = this.createColorChoices();
      this.locale = new Intl.NumberFormat('pt-BR', {style:'currency',currency:data.currency || 'BRL'});
      this.bind();
      if(!this.products.length) this.root.querySelector('[data-kit-empty]')?.removeAttribute('hidden');
      this.renderTiers();
      this.renderSlots();
      this.renderSummary();
      this.placeAndHideNativeControls();
    }

    get count() { return this.tiers[this.tierIndex]?.count || 1; }
    getProduct(id) { return this.products.find((p) => String(p.id) === String(id)) || null; }
    indexOfOption(product, names) {
      return (product.options || []).findIndex((name) => names.includes(normalize(name)));
    }
    sizeIndex(product) { return this.indexOfOption(product, ['tamanho','size','tam','talla']); }
    colorIndex(product) { return this.indexOfOption(product, ['cor','color','colour','cores','colores']); }

    createColorChoices() {
      const list = [];
      const keys = new Set();
      for (const product of this.products) {
        const cIndex = this.colorIndex(product);
        const distinct = cIndex >= 0 ? [...new Set(product.variants.map((v) => String(optionAt(v,cIndex) || '')).filter(Boolean))] : [null];
        for (const color of distinct) {
          const key = String(product.id) + '|' + normalize(color || '');
          if (keys.has(key)) continue;
          keys.add(key);
          const variants = product.variants.filter((v) => cIndex < 0 || normalize(optionAt(v,cIndex)) === normalize(color));
          const firstAvailable = variants.find((v) => v.available);
          list.push({
            key, productId:product.id, color: color || null,
            label:color || product.label || product.title,
            colorHex:product.swatchSource === 'color' ? product.colorHex : (COLORS[normalize(color || product.label)] || null),
            image: firstAvailable?.image || product.image || product.images?.[0] || null,
            available:!!firstAvailable, badge:product.badge || ''
          });
        }
      }
      return list;
    }

    variantsFor(draft) {
      const product = this.getProduct(draft?.productId);
      if (!product) return [];
      const ci = this.colorIndex(product);
      return product.variants.filter((variant) => ci<0 || normalize(optionAt(variant,ci)) === normalize(draft.color));
    }

    findVariant(draft) {
      if (!draft) return null;
      const product=this.getProduct(draft.productId);
      if (!product) return null;
      const variants=this.variantsFor(draft);
      const si=this.sizeIndex(product);
      if (si>=0) {
        if (!draft.size) return null;
        const matching=variants.filter((v) => normalize(optionAt(v,si))===normalize(draft.size));
        return matching.find((v)=>v.available) || matching[0] || null;
      }
      return variants.find((v)=>v.available) || variants[0] || null;
    }

    validVariant(draft) {
      const variant=this.findVariant(draft);
      return variant?.available ? variant : null;
    }

    sizeAvailable(size, product, color) {
      const si=this.sizeIndex(product);
      if (si<0) return (product.variants || []).some((v)=>v.available);
      const ci=this.colorIndex(product);
      return (product.variants || []).some((v)=>v.available && normalize(optionAt(v,si))===normalize(size) && (ci<0 || normalize(optionAt(v,ci))===normalize(color)));
    }

    firstSizeFor(product,color,preferred) {
      if (this.sizeIndex(product)<0) return null;
      if(preferred) {
        if(this.sizeAvailable(preferred,product,color))return preferred;
        return null;
      }
      const ci=this.colorIndex(product),si=this.sizeIndex(product);
      return product.variants.find((v)=>v.available && (ci<0 || normalize(optionAt(v,ci))===normalize(color)))?.['option'+(si+1)] || null;
    }

    renderTiers() {
      this.root.querySelectorAll('[data-kit-tier]').forEach((button) => {
        const index=Number(button.dataset.kitTier);
        const tier=this.tiers[index];
        if(!tier)return;
        const active=index===this.tierIndex;
        button.classList.toggle('is-selected',active);
        button.setAttribute('aria-pressed',String(active));
        const prices=button.querySelector('[data-kit-tier-pricing]');
        const before=button.querySelector('[data-kit-tier-before]');
        const after=button.querySelector('[data-kit-tier-after]');
        const saving=button.querySelector('[data-kit-tier-savings]');
        const subtitle=button.querySelector('.ivr-kit-builder__tier-sub');
        const hasBefore=Number.isInteger(tier.compareCents)&&tier.compareCents>0;
        const hasAfter=Number.isInteger(tier.saleCents)&&tier.saleCents>0;
        if(prices)prices.hidden=!hasBefore && !hasAfter;
        if(before) {
          before.hidden=!hasBefore;
          if(hasBefore)before.textContent='De '+this.locale.format(tier.compareCents/100);
        }
        if(after) {
          after.hidden=!hasAfter;
          if(hasAfter)after.textContent='Por '+this.locale.format(tier.saleCents/100);
        }
        if(saving) {
          const isSaving=hasBefore && hasAfter && tier.saleCents<tier.compareCents;
          saving.hidden=!isSaving;
          if(isSaving) {
            const percent=Math.round(100*(tier.compareCents-tier.saleCents)/tier.compareCents);
            saving.textContent=(this.root.dataset.savingsText||'Economize {percent}%').replace(/\{percent\}/g,(this.root.dataset.savingsPercent||'').trim()||percent).replace(/\{saved\}/g,this.locale.format((tier.compareCents-tier.saleCents)/100));
          }
        }
        if(subtitle)subtitle.hidden=hasAfter;
      });
    }

    renderSlots() {
      const container=this.root.querySelector('[data-kit-slots]');
      if (!container) return;
      this.root.dataset.kitCount=String(this.count);
      const previousFocus=document.activeElement?.dataset?.kitSlot;
      container.replaceChildren();
      for(let index=0;index<this.count;index+=1) {
        const slot=this.slots[index];
        const variant=this.validVariant(slot);
        const product=slot&&this.getProduct(slot.productId);
        const choice=slot&&this.colorChoices.find((x)=>x.key===slot.colorKey);
        const selected=!!variant;
        const button=el('button','ivr-kit-builder__slot'+(selected?' is-filled':''),null);
        button.type='button';
        button.dataset.kitSlot=String(index);
        button.setAttribute('aria-label',selected
          ? 'Editar peça '+(index+1)+': '+(choice?.label || product?.label || product?.title || '')+(slot.size ? ', '+slot.size : '')
          : 'Escolher peça '+(index+1));
        if (slot && !selected) button.classList.add('is-invalid');
        const visual=el('span','ivr-kit-builder__slot-visual');
        if (selected) {
          const image=this.galleryFor(slot)[0];
          if (image) {
            const img=el('img','ivr-kit-builder__slot-image');
            img.src=image;
            img.alt=choice?.label || product?.label || product?.title || 'Peça do kit';
            img.style.objectFit=product?.imageFit || 'contain';
            img.style.objectPosition=product?.imagePosition || 'center';
            img.loading='lazy';
            img.decoding='async';
            visual.appendChild(img);
          } else {
            visual.appendChild(el('span','ivr-kit-builder__slot-number',index+1));
          }
        } else {
          visual.appendChild(el('span','ivr-kit-builder__slot-number',index+1));
        }
        const detail=el('span','ivr-kit-builder__slot-detail');
        const name=el('span','ivr-kit-builder__slot-name',
          selected ? (choice?.label || product?.label || product?.title || '') : this.config.emptyLabel || 'Escolher');
        detail.appendChild(name);
        if(selected && slot.size) detail.appendChild(el('span','ivr-kit-builder__slot-size',slot.size));
        if(selected) detail.appendChild(el('span','ivr-kit-builder__slot-edit',this.config.editLabel || 'Alterar'));
        if(slot && !selected) detail.appendChild(el('span','ivr-kit-builder__slot-alert',this.config.unavailableMessage || 'Seleção indisponível'));
        button.append(visual,detail);
        container.appendChild(button);
      }
      if(previousFocus !== undefined) {
        const replacement=container.querySelector('[data-kit-slot="'+previousFocus+'"]');
        replacement?.focus({preventScroll:true});
      }
    }

    renderSummary() {
      const selected=this.slots.slice(0,this.count).map((slot)=>this.validVariant(slot));
      const complete=selected.length===this.count && selected.every(Boolean);
      const selectedCount=selected.filter(Boolean).length;
      const subtotal=selected.reduce((sum,item)=>sum+Number(item?.price || 0),0);
      const total=this.root.querySelector('[data-kit-total]');
      if(total)total.textContent=this.locale.format(subtotal/100);
      const tier=this.tiers[this.tierIndex];
      const beforeTotal=this.root.querySelector('[data-kit-summary-compare]');
      const compare=Number.isInteger(tier?.compareCents)&&tier.compareCents>subtotal;
      if(beforeTotal) {
        beforeTotal.hidden=!compare || !complete;
        if(compare && complete)beforeTotal.textContent='De '+this.locale.format(tier.compareCents/100);
      }
      const configuredSale=Number.isInteger(tier?.saleCents) && tier.saleCents>0 ? tier.saleCents : null;
      const priceNotice=this.root.querySelector('[data-kit-price-notice]');
      const mismatch=complete && configuredSale!==null && configuredSale!==subtotal;
      if(priceNotice) {
        priceNotice.hidden=!mismatch;
        if(mismatch)priceNotice.textContent='Valor promocional configurado: '+this.locale.format(configuredSale/100)+
          '. O total acima soma os preços das variantes, antes de descontos de carrinho. Confirme a oferta nas regras da Shopify.';
      }
      const progress=this.root.querySelector('[data-kit-progress]');
      if(progress)progress.textContent=selectedCount+' / '+this.count;
      const bar=this.root.querySelector('[data-kit-progress-bar]');
      if(bar)bar.style.width=(this.count?selectedCount/this.count*100:0)+'%';
      const helper=this.root.querySelector('[data-kit-helper]');
      if(helper)helper.textContent=complete ? (this.config.readyLabel || 'Seu kit está pronto.') :
        (this.config.pendingLabel || 'Escolha todas as peças para continuar.');
      const add=this.root.querySelector('[data-kit-checkout]');
      if(add) {
        add.disabled=!complete || this.busy;
        add.textContent=this.busy ? (this.config.loadingLabel || 'Adicionando...') : (this.config.checkoutLabel || 'Adicionar kit ao carrinho');
        add.setAttribute('aria-busy',String(this.busy));
      }
      const summary=this.root.querySelector('[data-kit-count-label]');
      if(summary)summary.textContent=this.count+' '+(this.count===1?(this.config.singlePiece || 'peça'):(this.config.pluralPiece || 'peças'));
    }

    galleryFor(draft) {
      const product=this.getProduct(draft?.productId);
      if(!product)return [];
      const variant=this.findVariant(draft);
      const images=(product.customImage
        ? [product.image,variant?.image,...(product.images || [])]
        : [variant?.image,product.image,...(product.images || [])]
      ).filter(Boolean);
      return [...new Set(images)];
    }

    openModal(index) {
      if(this.busy || !this.dialog || index>=this.count || index<0) return;
      if(!this.colorChoices.length) {
        this.showError('Adicione produtos aos blocos do seletor no editor da Shopify.');
        return;
      }
      this.modalSlot=index;
      this.lastModalSlot=index;
      this.nextModalSlot=-1;
      this.draft=this.slots[index] ? {...this.slots[index]} : null;
      if(!this.draft) {
        const choice=this.colorChoices.find((option)=>option.available);
        if(choice) {
          const product=this.getProduct(choice.productId);
          this.draft={
            productId:choice.productId,colorKey:choice.key,color:choice.color,
            size:this.firstSizeFor(product,choice.color,null)
          };
        }
      }
      this.slideIndex=0;
      this.clearModalError();
      this.renderModal();
      if(typeof this.dialog.showModal==='function')this.dialog.showModal();
      else this.dialog.setAttribute('open','');
      this.dialog.querySelector('[data-kit-modal-close]')?.focus({preventScroll:true});
    }

    renderModal() {
      if(!this.dialog)return;
      const colorRoot=this.dialog.querySelector('[data-kit-modal-colors]');
      const sizeRoot=this.dialog.querySelector('[data-kit-modal-sizes]');
      const currentChoice=this.colorChoices.find((choice)=>choice.key===this.draft?.colorKey);
      const currentProduct=this.getProduct(this.draft?.productId);
      const badge=this.dialog.querySelector('[data-kit-modal-badge]');
      if(badge){
        const label=this.config.showProductBadges ? String(currentProduct?.badge || '') : '';
        badge.textContent=label;
        badge.hidden=!label;
      }
      const cLabel=this.dialog.querySelector('[data-kit-modal-color-current]');
      if(cLabel)cLabel.textContent=currentChoice?.label || 'Selecione';
      const sLabel=this.dialog.querySelector('[data-kit-modal-size-current]');
      if(sLabel)sLabel.textContent=this.draft?.size || 'Selecione';
      colorRoot?.replaceChildren();
      sizeRoot?.replaceChildren();
      for(const choice of this.colorChoices) {
        const button=el('button','ivr-kit-builder__color-choice',null);
        button.type='button';
        button.dataset.modalColor=choice.key;
        button.disabled=!choice.available;
        button.classList.toggle('is-selected',choice.key===this.draft?.colorKey);
        button.setAttribute('aria-pressed',String(choice.key===this.draft?.colorKey));
        button.setAttribute('aria-label','Cor: '+choice.label+(choice.available?'':', indisponível'));
        button.title=choice.label;
        if(choice.colorHex)button.style.backgroundColor=choice.colorHex;
        else if(choice.image){
          button.style.backgroundImage='url("'+String(choice.image).replace(/"/g,'%22')+'")';
          button.style.backgroundSize='cover';
          button.style.backgroundPosition='center';
        }
        colorRoot?.appendChild(button);
      }
      if(currentProduct) {
        const si=this.sizeIndex(currentProduct);
        if(si>=0) {
          this.dialog.querySelector('[data-kit-modal-size-area]')?.removeAttribute('hidden');
          const sizes=[...new Set(currentProduct.variants.map((v)=>optionAt(v,si)).filter(Boolean))];
          sizes.sort((a,b)=>{
            const ai=preferredSizes.indexOf(String(a).toUpperCase()),bi=preferredSizes.indexOf(String(b).toUpperCase());
            if(ai>=0 && bi>=0)return ai-bi;
            if(ai>=0)return -1;if(bi>=0)return 1;
            return String(a).localeCompare(String(b),'pt-BR',{numeric:true});
          });
          for(const size of sizes) {
            const button=el('button','ivr-kit-builder__modal-size',size);
            button.type='button';
            button.dataset.modalSize=String(size);
            button.disabled=!this.sizeAvailable(size,currentProduct,this.draft.color);
            const selected=normalize(this.draft.size)===normalize(size);
            button.classList.toggle('is-selected',selected);
            button.setAttribute('aria-pressed',String(selected));
            sizeRoot?.appendChild(button);
          }
        } else {
          this.dialog.querySelector('[data-kit-modal-size-area]')?.setAttribute('hidden','');
        }
      }else{
        this.dialog.querySelector('[data-kit-modal-size-area]')?.setAttribute('hidden','');
      }
      const confirm=this.dialog.querySelector('[data-kit-modal-confirm]');
      if(confirm) {
        confirm.disabled=!this.validVariant(this.draft);
        confirm.textContent=this.slots[this.modalSlot] ? (this.config.modalUpdate || 'ATUALIZAR PEÇA') : (this.config.modalAdd || 'ADICIONAR AO KIT');
      }
      this.renderGallery();
    }

    renderGallery() {
      const gallery=this.dialog?.querySelector('[data-kit-gallery]');
      if(!gallery)return;
      gallery.replaceChildren();
      const images=this.galleryFor(this.draft);
      const count=(this.config.galleryMode==='single' || images.length<2)?1:2;
      for(let i=0;i<count;i+=1) {
        const frame=el('div','ivr-kit-builder__gallery-frame');
        const src=images[(this.slideIndex+i)%images.length];
        if(src) {
          const image=el('img','ivr-kit-builder__gallery-image');
          image.src=src;
          image.alt=(this.colorChoices.find((x)=>x.key===this.draft?.colorKey)?.label || 'Produto')+' - foto '+(this.slideIndex+i+1);
          image.decoding='async';
          image.loading='eager';
          frame.appendChild(image);
        }else{
          frame.appendChild(el('span','ivr-kit-builder__gallery-empty','Sem imagem'));
        }
        gallery.appendChild(frame);
      }
      const prev=this.dialog.querySelector('[data-kit-gallery-prev]');
      const next=this.dialog.querySelector('[data-kit-gallery-next]');
      const show=images.length>count;
      if(prev)prev.hidden=!show;
      if(next)next.hidden=!show;
      gallery.classList.toggle('is-single',count===1);
      const status=this.dialog.querySelector('[data-kit-gallery-status]');
      if(status)status.textContent=images.length ? String(this.slideIndex+1)+' / '+images.length : '';
    }

    chooseColor(key) {
      const choice=this.colorChoices.find((option)=>option.key===key);
      if(!choice || !choice.available)return;
      const prevSize=this.draft?.size || null;
      const product=this.getProduct(choice.productId);
      this.draft={productId:choice.productId,colorKey:choice.key,color:choice.color,
        size:this.firstSizeFor(product,choice.color,prevSize)};
      this.slideIndex=0;
      this.clearModalError();
      this.renderModal();
      const chosen=[...this.dialog.querySelectorAll('[data-modal-color]')].find((b)=>b.dataset.modalColor===key);
      chosen?.focus({preventScroll:true});
    }

    chooseSize(value) {
      if(!this.draft)return;
      this.draft.size=value;
      this.clearModalError();
      this.renderModal();
      const chosen=[...this.dialog.querySelectorAll('[data-modal-size]')].find((b)=>b.dataset.modalSize===value);
      chosen?.focus({preventScroll:true});
    }

    commitModal() {
      if(this.modalSlot<0 || !this.validVariant(this.draft)) {
        this.showModalError(this.config.unavailableMessage || 'Selecione uma cor e um tamanho disponíveis.');
        return;
      }
      const index=this.modalSlot;
      this.slots[index]={...this.draft};
      this.nextModalSlot = this.config.autoOpenNext
        ? this.slots.slice(0,this.count).findIndex((slot)=>!this.validVariant(slot))
        : -1;
      this.modalSlot=-1;
      this.dialog.close();
      this.renderSlots();
      this.renderSummary();
    }

    showModalError(text) {
      const node=this.dialog?.querySelector('[data-kit-modal-error]');
      if(node)node.textContent=text;
    }
    clearModalError() {
      const node=this.dialog?.querySelector('[data-kit-modal-error]');
      if(node)node.textContent='';
    }

    setTier(index) {
      if(this.busy || index<0 || index>=this.tiers.length)return;
      this.tierIndex=index;
      this.renderTiers();
      this.renderSlots();
      this.renderSummary();
    }

    showError(text) {
      const node=this.root.querySelector('[data-kit-error]');
      if(!node)return;
      node.textContent=text;
      node.hidden=false;
      node.focus({preventScroll:true});
    }
    clearError() {
      const node=this.root.querySelector('[data-kit-error]');
      if(node){node.textContent='';node.hidden=true;}
    }

    async checkout() {
      if(this.busy)return;
      const variants=this.slots.slice(0,this.count).map((slot)=>this.validVariant(slot));
      if(variants.length!==this.count || variants.some((v)=>!v)) {
        this.showError(this.config.unavailableMessage || 'Finalize todas as peças antes de comprar.');
        return;
      }
      const grouped=new Map();
      for(const variant of variants) {
        const id=String(variant.id);
        if(!grouped.has(id))grouped.set(id,{id:Number(variant.id),quantity:0});
        grouped.get(id).quantity++;
      }
      this.busy=true;
      this.clearError();
      this.renderSummary();
      try {
        const prefix=(window.Shopify?.routes?.root || '/').replace(/\/?$/,'/');
        const response=await fetch(prefix+'cart/add.js',{
          method:'POST',
          headers:{'Accept':'application/json','Content-Type':'application/json','X-Requested-With':'XMLHttpRequest'},
          credentials:'same-origin',
          body:JSON.stringify({items:[...grouped.values()]})
        });
        const result=await response.json().catch(()=>({}));
        if(!response.ok)throw new Error(result.description || result.message || 'Não foi possível adicionar as peças ao carrinho.');
        const checkoutButton=this.root.querySelector('[data-kit-checkout]');
        if(checkoutButton)checkoutButton.textContent=this.config.successLabel || 'ADICIONADO!';
        try{sessionStorage.setItem('ivr-kit-builder-open-cart','yes');}catch(_error){}
        window.location.reload();
      } catch(error) {
        this.busy=false;
        this.renderSummary();
        this.showError(error?.message || 'Não foi possível adicionar as peças ao carrinho.');
      }
    }

    placeAndHideNativeControls() {
      const sectionRoot=this.root.closest('.shopify-section');
      const productInfo=sectionRoot?.parentElement?.querySelector('.product__info-container') || document.querySelector('.product__info-container');
      if(!productInfo)return;
      if(this.root.dataset.autoPosition==='true') {
        const heading=productInfo.querySelector('.product__title') || productInfo.querySelector('h1');
        if(heading)heading.insertAdjacentElement('afterend',this.root);
      }
      if(this.root.dataset.hideNative==='true') {
        this.nativeHidden=[];
        productInfo.querySelectorAll('variant-selects,variant-radios,.product-form__quantity').forEach((node)=>{
          if(!this.root.contains(node)) {
            this.nativeHidden.push({node,display:node.style.display});
            node.style.display='none';
          }
        });
        const form=productInfo.querySelector('product-form');
        if(form && !this.root.contains(form)) {
          this.nativeHidden.push({node:form,display:form.style.display});
          form.style.display='none';
        }
      }
    }

    bind() {
      const signal=this.abort.signal;
      this.root.addEventListener('click',(event)=>{
        const target=event.target;
        const tier=target.closest?.('[data-kit-tier]');
        if(tier){this.setTier(Number(tier.dataset.kitTier));return;}
        const slot=target.closest?.('[data-kit-slot]');
        if(slot){this.openModal(Number(slot.dataset.kitSlot));return;}
        if(target.closest?.('[data-kit-checkout]')){this.checkout();return;}
        if(target.closest?.('[data-kit-modal-close]')){this.dialog.close();return;}
        const color=target.closest?.('[data-modal-color]');
        if(color){this.chooseColor(color.dataset.modalColor);return;}
        const size=target.closest?.('[data-modal-size]');
        if(size){this.chooseSize(size.dataset.modalSize);return;}
        if(target.closest?.('[data-kit-gallery-prev]')){
          this.slideIndex=(this.slideIndex-1+this.galleryFor(this.draft).length)%Math.max(1,this.galleryFor(this.draft).length);
          this.renderGallery();return;
        }
        if(target.closest?.('[data-kit-gallery-next]')){
          this.slideIndex=(this.slideIndex+1)%Math.max(1,this.galleryFor(this.draft).length);
          this.renderGallery();return;
        }
        if(target.closest?.('[data-kit-modal-confirm]')){this.commitModal();return;}
      },{signal});
      this.dialog?.addEventListener('click',(event)=>{
        if(event.target===this.dialog)this.dialog.close();
      },{signal});
      this.dialog?.addEventListener('close',()=>{
        const focusIndex=this.modalSlot<0?this.lastModalSlot:this.modalSlot;
        const next=this.nextModalSlot;
        this.modalSlot=-1;
        this.draft=null;
        this.nextModalSlot=-1;
        if(next>=0) {
          this.openModal(next);
        } else {
          this.root.querySelector('[data-kit-slot="'+focusIndex+'"]')?.focus({preventScroll:true});
        }
      },{signal});
      this.root.addEventListener('keydown',(event)=>{
        if(event.key!=='ArrowLeft' && event.key!=='ArrowRight')return;
        const button=event.target.closest?.('button');
        const group=button?.closest?.('[data-kit-tiers],[data-kit-modal-colors],[data-kit-modal-sizes]');
        if(!group)return;
        const options=[...group.querySelectorAll('button:not(:disabled)')];
        const index=options.indexOf(button);
        if(index<0||options.length<2)return;
        event.preventDefault();
        options[(index+(event.key==='ArrowRight'?1:-1)+options.length)%options.length].focus({preventScroll:true});
      },{signal});
    }

    destroy() {
      this.abort.abort();
      if(this.dialog?.open)this.dialog.close();
      this.nativeHidden?.forEach(({node,display})=>{node.style.display=display;});
      delete this.root.dataset.ivrKitReady;
    }
  }

  const registry=new WeakMap();
  const init=(scope=document)=>{
    const roots=[];
    if(scope.matches?.(ROOT))roots.push(scope);
    scope.querySelectorAll?.(ROOT).forEach((root)=>roots.push(root));
    for(const root of roots) {
      if(root.dataset.ivrKitReady==='true')continue;
      const origin=root.dataset.kitSectionId;
      if(origin) {
        [...document.querySelectorAll(ROOT)].forEach((previous)=>{
          if(previous!==root && previous.dataset.kitSectionId===origin && previous.dataset.ivrKitReady==='true') {
            registry.get(previous)?.destroy();
            previous.remove();
          }
        });
      }
      const container=root.closest('.shopify-section');
      const configNode=container?.querySelector('[id="'+root.dataset.kitConfigId+'"]') || document.getElementById(root.dataset.kitConfigId);
      if(!configNode)continue;
      try {
        const data=JSON.parse(configNode.textContent);
        if(!Array.isArray(data.tiers) || !data.tiers.length)continue;
        const instance=new KitBuilder(root,data);
        registry.set(root,instance);
        root.dataset.ivrKitReady='true';
      } catch (error) {
        root.querySelector('[data-kit-error]')?.removeAttribute('hidden');
        const node=root.querySelector('[data-kit-error]');
        if(node)node.textContent='Não foi possível carregar o seletor. Atualize a página.';
        console.error('[IVR Kit Builder]',error);
      }
    }
  };
  const destroy=(scope)=>{
    const roots=[];
    if(scope.matches?.(ROOT))roots.push(scope);
    scope.querySelectorAll?.(ROOT).forEach((root)=>roots.push(root));
    scope.querySelectorAll?.('[data-ivr-kit-origin]').forEach((marker)=>{
      document.querySelectorAll(ROOT).forEach((root)=>{
        if(root.dataset.kitSectionId===marker.dataset.ivrKitOrigin && !roots.includes(root))roots.push(root);
      });
    });
    roots.forEach((root)=>{
      registry.get(root)?.destroy();
      if(!scope.contains?.(root))root.remove();
    });
  };
  const openCartAfterReload=()=>{
    try {
      if(sessionStorage.getItem('ivr-kit-builder-open-cart')!=='yes')return;
      sessionStorage.removeItem('ivr-kit-builder-open-cart');
    }catch(_error){return;}
    let attempts=0;
    const tryOpen=()=>{
      const drawer=document.querySelector('cart-drawer');
      const icon=document.getElementById('cart-icon-bubble');
      if(drawer && typeof drawer.open==='function'){
        try{drawer.open(icon || undefined);}catch(_error){drawer.open();}
        return;
      }
      if(++attempts<30)setTimeout(tryOpen,150);
      else window.location.assign((window.Shopify?.routes?.root || '/')+'cart');
    };
    setTimeout(tryOpen,350);
  };

  window.IVRKitBuilderV3={init,destroy};
  if(document.readyState==='loading'){
    document.addEventListener('DOMContentLoaded',()=>{init(document);openCartAfterReload();},{once:true});
  }else{
    init(document);openCartAfterReload();
  }
  document.addEventListener('shopify:section:load',(event)=>init(event.target));
  document.addEventListener('shopify:section:unload',(event)=>destroy(event.target));
})();